import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Stack,
  Divider,
  Card,
  CardMedia,
  CardContent,
} from '@mui/material';
import {
  Close,
  Add,
  Remove,
  Delete,
  ShoppingBag,
  ArrowForward,
} from '@mui/icons-material';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';

const Cart = () => {
  const navigate = useNavigate();
  const {
    cartItems,
    isCartOpen,
    setIsCartOpen,
    updateQuantity,
    removeFromCart,
    getCartTotal,
    getFormattedCartTotal,
    clearCart,
  } = useCart();

  const handleClose = () => {
    setIsCartOpen(false);
  };

  const handleCheckout = () => {
    setIsCartOpen(false);
    navigate('/checkout');
  };

  return (
    <Drawer
      anchor="right"
      open={isCartOpen}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 420 },
          bgcolor: '#F5F2EB',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box className='head-text'
          sx={{
            p: 3,
            bgcolor: 'white',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <ShoppingBag sx={{ fontSize: 28, color: 'primary.main' }} />
              <Typography
                variant="h5"
                sx={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 700,
                }}
              >
                Shopping Cart
              </Typography>
            </Stack>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Stack>
        </Box>

        {/* Cart Items */}
        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
          {cartItems.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
              }}
            >
              <ShoppingBag sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Your cart is empty
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add some wellness products to get started
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {cartItems.map((item) => (
                <Card key={item.itemId} sx={{ display: 'flex' }}>
                  <CardMedia
                    component="img"
                    sx={{ width: 100, objectFit: 'cover' }}
                    image={item.image}
                    alt={item.name}
                  />
                  <CardContent sx={{ flex: 1, p: 2 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        mb: 0.5,
                      }}
                    >
                      {item.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0.5, fontSize: '0.8rem' }}
                    >
                      {item.category}
                    </Typography>
                    {item.selectedVariant && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          color: 'primary.main',
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        {item.selectedVariant.weight} - {item.selectedVariant.packaging}
                      </Typography>
                    )}
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography
                        className='font13'
                        variant="h6"
                        sx={{
                          color: 'primary.main',
                          fontWeight: 700,
                          fontSize: '1.1rem',
                        }}
                      >
                        {item.selectedVariant?.currencySymbol || '$'}{(item.price * item.quantity).toFixed(2)}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <IconButton
                          size="small"
                          className='add-prd'
                          onClick={() => updateQuantity(item.itemId, item.quantity - 1)}
                          sx={{
                            bgcolor: 'primary.main',
                            color: 'white',
                            '&:hover': { bgcolor: 'primary.dark' },
                            width: 28,
                            height: 28,
                          }}
                        >
                          <Remove fontSize="small" />
                        </IconButton>
                        <Typography className='ml-5' sx={{ minWidth: 20, textAlign: 'center', fontWeight: 600 }}>
                          {item.quantity}
                        </Typography>
                        <IconButton
                          size="small"
                          className='add-prd ml-5'
                          onClick={() => updateQuantity(item.itemId, item.quantity + 1)}
                          sx={{
                            bgcolor: 'primary.main',
                            color: 'white',
                            '&:hover': { bgcolor: 'primary.dark' },
                            width: 28,
                            height: 28,
                          }}
                        >
                          <Add fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          className='ml-2'
                          onClick={() => removeFromCart(item.itemId)}
                          sx={{
                            color: 'error.main',
                            '&:hover': { bgcolor: 'error.light', color: 'white' },
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </Box>

        {/* Footer */}
        {cartItems.length > 0 && (
          <Box
            sx={{
              p: 3,
              bgcolor: 'white',
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Total:
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontWeight: 700,
                    color: 'primary.main',
                  }}
                >
                  {getFormattedCartTotal()}
                </Typography>
              </Stack>

              <Divider />

              <Button
                className='form-btn'
                variant="contained"
                size="large"
                fullWidth
                endIcon={<ArrowForward />}
                onClick={handleCheckout}
                sx={{
                  py: 1.5,
                  borderRadius: 50,
                  background: 'linear-gradient(135deg, #C9A961 0%, #B89850 100%)',
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #B89850 0%, #A88740 100%)',
                  },
                }}
              >
                Proceed to Checkout
              </Button>

              <Button
                variant="text"
                size="small"
                onClick={clearCart}
                sx={{
                  color: 'text.secondary',
                  textTransform: 'none',
                  '&:hover': { color: 'error.main' },
                }}
              >
                Clear Cart
              </Button>
            </Stack>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default Cart;
